<template>
    <div>
        <div class="text-body-1 mb-4">
            Select how funnycharts should find your network interface for Mabinogi packet capture.
        </div>

        <v-radio-group v-model="mode" inline class="mb-1">
            <v-radio label="Auto-detect (recommended)" value="auto"></v-radio>
            <v-radio label="Manual selection" value="manual"></v-radio>
        </v-radio-group>

        <div v-if="mode === 'auto'" class="text-caption text-medium-emphasis mb-4">
            Scans all interfaces for Mabinogi traffic. Mabinogi must be running and connected to a server.
        </div>

        <div v-else class="mb-4">
            <v-select
                v-model="selectedNic"
                :items="nicItems"
                item-title="label"
                item-value="name"
                label="Network Interface"
                :loading="loadingNics"
                no-data-text="No interfaces found"
                density="compact"
                variant="outlined">
            </v-select>
        </div>

        <!-- Capture mode selector -->
        <v-select
            v-model="captureMode"
            :items="captureModeItems"
            item-title="label"
            item-value="value"
            label="Capture Mode"
            density="compact"
            variant="outlined"
            class="mb-1">
        </v-select>
        <div class="text-caption text-medium-emphasis mb-4">
            <span v-if="captureMode === 'normal'">Direct connection — filters by known game-server IPs and ports.</span>
            <span v-else-if="captureMode === 'exitlag'">ExitLag — captures all TCP and strips ExitLag transport headers to extract game packets.</span>
            <span v-else-if="captureMode === 'vpn'">VPN / Mudfish — works whether you capture on your normal NIC (tunneled game traffic is unwrapped automatically) or on Mudfish's own virtual adapter.</span>
        </div>

        <v-btn
            @click="save"
            block
            color="primary"
            :loading="saving"
            :disabled="mode === 'manual' && !selectedNic">
            {{ mode === 'auto' ? 'Detect &amp; Save' : 'Save' }}
        </v-btn>

        <v-alert v-if="errorMsg" type="error" density="compact" class="mt-3" closable @click:close="errorMsg = ''">
            {{ errorMsg }}
        </v-alert>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';

interface NicDevice {
    Name: string;
    Description: string;
    Addresses: string[];
}

export default defineComponent({
    name: 'NicSetup',
    emits: ['done'],
    setup(_, { emit }) {
        const mode = ref<'auto' | 'manual'>('auto');
        const selectedNic = ref('');
        const captureMode = ref<'normal' | 'exitlag' | 'vpn'>('normal');
        const captureModeItems = [
            { value: 'normal',  label: 'Normal (direct connection)' },
            { value: 'exitlag', label: 'ExitLag' },
            { value: 'vpn',     label: 'VPN / Mudfish' },
        ];
        const nics = ref<NicDevice[]>([]);
        const loadingNics = ref(false);
        const saving = ref(false);
        const errorMsg = ref('');

        const nicItems = computed(() =>
            nics.value.map(n => ({
                name: n.Name,
                label: n.Description ? `${n.Description} (${n.Name})` : n.Name,
            }))
        );

        const fetchNics = async () => {
            loadingNics.value = true;
            try {
                const [nicRes, cfgRes] = await Promise.all([
                    fetch('/api/nic/list'),
                    fetch('/api/nic/config'),
                ]);
                if (nicRes.ok) nics.value = await nicRes.json() ?? [];
                if (cfgRes.ok) {
                    const cfg = await cfgRes.json();
                    if (cfg.capture_mode === 'exitlag' || cfg.capture_mode === 'vpn') {
                        captureMode.value = cfg.capture_mode;
                    }
                    if (cfg.mode === 'auto' || cfg.mode === 'manual') {
                        mode.value = cfg.mode;
                    }
                    if (cfg.name) {
                        selectedNic.value = cfg.name;
                    }
                }
            } catch (e) {
                console.error('Failed to fetch NICs:', e);
            } finally {
                loadingNics.value = false;
            }
        };

        const save = async () => {
            saving.value = true;
            errorMsg.value = '';
            try {
                const body = mode.value === 'auto'
                    ? { mode: 'auto', name: '', capture_mode: captureMode.value }
                    : { mode: 'manual', name: selectedNic.value, capture_mode: captureMode.value };

                const res = await fetch('/api/nic/config', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });

                if (!res.ok) {
                    const txt = await res.text();
                    throw new Error(txt);
                }

                emit('done');
            } catch (e: any) {
                errorMsg.value = e.message ?? 'Failed to save NIC configuration.';
            } finally {
                saving.value = false;
            }
        };

        watch(mode, (val) => {
            if (val === 'manual' && nics.value.length === 0) {
                fetchNics();
            }
        });

        onMounted(fetchNics);

        return { mode, selectedNic, captureMode, captureModeItems, nicItems, loadingNics, saving, errorMsg, save };
    },
});
</script>
